/**
 * Centralized MEV localStorage utility with environment-aware key management
 * 
 * This utility solves the systemic issue of localStorage key mismatches between
 * dev and production builds by providing a unified API with automatic fallbacks.
 * 
 * Usage:
 *   import { mevStorage } from './mevStorage';
 *   const storeId = mevStorage.getStoreId();
 *   const transHeader = mevStorage.getTransactionHeader();
 */

class MEVStorage {
    /**
     * Get current environment from localStorage
     * Returns uppercase environment (DEV, PROD, etc.)
     */
    private getEnvironment(): string {
        return this.getEnvirn().toUpperCase();
    }

    /**
     * Generate environment-specific key
     * Example: getEnvKey('transHeader') => 'transHeader_dev'
     */
    private getEnvKey(prefix: string): string {
        const env = this.getEnvironment().toLowerCase();
        return `${prefix}_${env}`;
    }

    /**
     * Get item with automatic fallback to multiple possible keys
     * Checks all common variations to ensure data is found
     */
    getItem(prefix: string): string | null {
        const env = this.getEnvironment().toLowerCase();
        const storeId = this.getStoreId() || "";
        const scoped = storeId ? `${prefix}_${storeId}_${env}` : "";
        const sameEnv = `${prefix}_${env}`;
        const saved = (scoped && localStorage.getItem(scoped)) || localStorage.getItem(sameEnv) || "";
        if (!saved) return null;
        if (scoped && !localStorage.getItem(scoped)) localStorage.setItem(scoped, saved);
        return saved;
    }

    /**
     * Set item with environment-specific key
     */
    setItem(prefix: string, value: string): void {
        const env = this.getEnvironment().toLowerCase();
        const storeId = this.getStoreId() || "";
        localStorage.setItem(`${prefix}_${env}`, value);
        if (storeId) localStorage.setItem(`${prefix}_${storeId}_${env}`, value);
    }

    /**
     * Remove item (all possible variations)
     */
    removeItem(prefix: string): void {
        const possibleKeys = [
            this.getEnvKey(prefix),
            `${prefix}_dev`,
            `${prefix}_DEV`,
            `${prefix}_prod`,
            `${prefix}_PROD`,
            prefix
        ];

        possibleKeys.forEach(key => localStorage.removeItem(key));
    }

    // ============================================================================
    // Convenience methods for common MEV data
    // ============================================================================

    /**
     * Get Store ID - checks all possible keys
     * Priority: selectedStore > selectedStoreId > currentStoreId
     */
    getStoreId(): string | null {
        return localStorage.getItem('selectedStore')
            || localStorage.getItem('selectedStoreId')
            || localStorage.getItem('currentStoreId')
            || null;
    }

    /**
     * Get transaction header with environment fallback
     */
    getTransactionHeader(): string | null {
        return this.getItem('transHeader');
    }

    /**
     * Set transaction header with environment-specific key
     */
    setTransactionHeader(value: string): void {
        this.setItem('transHeader', value);
    }

    /**
     * Get company info with environment fallback
     */
    getCompanyInfo(): string | null {
        return this.getItem('company_info');
    }

    /**
     * Set company info with environment-specific key
     */
    setCompanyInfo(value: string): void {
        this.setItem('company_info', value);
    }

    /**
     * Get environment value
     */
    getEnvirn(): string {
        const storeId = this.getStoreId();
        if (storeId) {
            const key = `cloudMevEnvirn_${storeId}`;
            const scoped = localStorage.getItem(key);
            if (scoped) return scoped;
            const loose = localStorage.getItem('cloudMevEnvirn') || localStorage.getItem('envirn');
            if (loose) {
                localStorage.setItem(key, loose);
                return loose;
            }
        }
        return localStorage.getItem('envirn') || 'DEV';
    }

    /**
     * Save the environment on the signed-in store.
     * A value saved without a store is copied onto that store the next time it is read.
     */
    setEnvirn(value: string): void {
        const storeId = this.getStoreId();
        if (storeId) localStorage.setItem(`cloudMevEnvirn_${storeId}`, value);
        localStorage.setItem('envirn', value);
    }

    private signatureKey(name: string): string {
        const storeId = this.getStoreId();
        if (!storeId) return '';
        const env = this.getEnvirn().toLowerCase();
        return `${name}_${storeId}_${env}`;
    }

    /**
     * The bill chain for this store and environment.
     * The first bill uses 88 equal signs. An accepted bill's signature is stored for the next bill.
     */
    getPreviousSignature(): string {
        const key = this.signatureKey('signa_preced');
        if (!key) return '='.repeat(88);
        return localStorage.getItem(key) || '='.repeat(88);
    }

    /**
     * Set previous signature for the signed-in store.
     */
    setPreviousSignature(value: string): void {
        const currentKey = this.signatureKey('signa_actu');
        const precedKey = this.signatureKey('signa_preced');
        const previousKey = this.signatureKey('signa_previous');
        if (!currentKey || !precedKey || !previousKey) return;
        const previous = localStorage.getItem(currentKey) || localStorage.getItem(precedKey) || '';
        if (previous) localStorage.setItem(previousKey, previous);
        localStorage.setItem(currentKey, value);
        localStorage.setItem(precedKey, value);
    }

    /**
     * The first bill in a test uses 88 equal signs.
     * Reset writes that value into the store keys the sale reads, including the older key that has no environment.
     */
    resetInitialSignature(storeId: string): void {
        const id = String(storeId || this.getStoreId() || '').trim();
        if (!id) return;
        const initial = '='.repeat(88);
        ['signa_actu', 'signa_preced', 'signa_previous'].forEach((name) => {
            localStorage.setItem(`${name}_${id}_essai`, initial);
            localStorage.setItem(`${name}_${id}`, initial);
            localStorage.setItem(`${name}_essai`, initial);
        });
    }

    /**
     * Get POS user data for a specific store
     */
    getPOSUser(storeId: string): string | null {
        return localStorage.getItem(`currentPOSUser_${storeId}`)
            || localStorage.getItem('pos_user')
            || null;
    }

    /**
     * Get operation logs with environment-specific key
     */
    getOperationLogs(suffix: string = ''): string | null {
        const env = this.getEnvironment().toLowerCase();
        const key = suffix
            ? `mev_operation_logs_${env}_${suffix}`
            : `mev_operation_logs_${env}`;
        return localStorage.getItem(key);
    }

    /**
     * Set operation logs with environment-specific key
     */
    setOperationLogs(value: string, suffix: string = ''): void {
        const env = this.getEnvironment().toLowerCase();
        const key = suffix
            ? `mev_operation_logs_${env}_${suffix}`
            : `mev_operation_logs_${env}`;
        localStorage.setItem(key, value);
    }

    /**
     * Get MEV terminals list
     */
    getTerminals(): string | null {
        const env = this.getEnvironment().toLowerCase();
        return localStorage.getItem(`mev_terminals_${env}`);
    }

    /**
     * Set MEV terminals list
     */
    setTerminals(value: string): void {
        const env = this.getEnvironment().toLowerCase();
        localStorage.setItem(`mev_terminals_${env}`, value);
    }

    /**
     * Get CSR form data
     */
    getCSRForm(): string | null {
        const env = this.getEnvironment().toLowerCase();
        return localStorage.getItem(`csr_form_${env}`);
    }

    /**
     * Set CSR form data
     */
    setCSRForm(value: string): void {
        const env = this.getEnvironment().toLowerCase();
        localStorage.setItem(`csr_form_${env}`, value);
    }

    /**
     * Get terminal CSR
     */
    getTerminalCSR(): string | null {
        const env = this.getEnvironment().toLowerCase();
        return localStorage.getItem(`terminal_csr_${env}`);
    }

    /**
     * Set terminal CSR
     */
    setTerminalCSR(value: string): void {
        const env = this.getEnvironment().toLowerCase();
        localStorage.setItem(`terminal_csr_${env}`, value);
    }

    /**
     * Get terminal private key
     */
    getTerminalPrivateKey(): string | null {
        const env = this.getEnvironment().toLowerCase();
        return localStorage.getItem(`terminal_privateKey_${env}`);
    }

    /**
     * Set terminal private key
     */
    setTerminalPrivateKey(value: string): void {
        const env = this.getEnvironment().toLowerCase();
        localStorage.setItem(`terminal_privateKey_${env}`, value);
    }

    /**
     * Get terminal public key
     */
    getTerminalPublicKey(): string | null {
        const env = this.getEnvironment().toLowerCase();
        return localStorage.getItem(`terminal_publicKey_${env}`);
    }

    /**
     * Set terminal public key
     */
    setTerminalPublicKey(value: string): void {
        const env = this.getEnvironment().toLowerCase();
        localStorage.setItem(`terminal_publicKey_${env}`, value);
    }

    /**
     * Get settings environment
     */
    getSettingsEnvirn(): string | null {
        return localStorage.getItem('Settings_envirn');
    }

    /**
     * Set settings environment
     */
    setSettingsEnvirn(value: string): void {
        localStorage.setItem('Settings_envirn', value);
    }

    /**
     * Get receipt text environment
     */
    getReceiptTextEnvirn(): string | null {
        return localStorage.getItem('receipt_text_envirn');
    }

    /**
     * Set receipt text environment
     */
    setReceiptTextEnvirn(value: string): void {
        localStorage.setItem('receipt_text_envirn', value);
    }

    /**
     * Get MEV current user ID
     */
    getMEVCurrentUserId(): string {
        return localStorage.getItem('mev_current_user_id') || '';
    }

    /**
     * Get MEV current user name
     */
    getMEVCurrentUserName(): string {
        return localStorage.getItem('mev_current_user_name')
            || localStorage.getItem('nomUtil')
            || 'POS User';
    }

    /**
     * Get MEV EOD preload data
     */
    getMEVEODPreloadData(): string | null {
        return localStorage.getItem('mev_eod_preload_data');
    }

    /**
     * Get store address
     */
    getStoreAddress(): string {
        return localStorage.getItem('currentStoreAddress') || '';
    }
}

// Export singleton instance
export const mevStorage = new MEVStorage();
